import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { CreditCard } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";

interface PaymentCard {
  id: string;
  brand: string;
  last_four_digits: string;
  exp_month: number;
  exp_year: number;
  holder_name: string;
  is_default: boolean;
}

interface AssinaturaCardsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
}

export function AssinaturaCardsModal({
  open,
  onOpenChange,
  userId
}: AssinaturaCardsModalProps) {
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState<PaymentCard[]>([]);

  useEffect(() => {
    if (open && userId) {
      loadCards();
    }
  }, [open, userId]);

  const loadCards = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("payment_cards")
        .select("*")
        .eq("user_id", userId)
        .order("is_default", { ascending: false });

      if (error) throw error;
      setCards(data || []);
    } catch (error) {
      console.error("Error loading cards:", error);
    } finally {
      setLoading(false);
    }
  };

  const getBrandIcon = (brand: string) => {
    return <CreditCard className="h-5 w-5" />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Cartões Salvos</DialogTitle>
          <DialogDescription>
            Cartões de crédito vinculados a esta assinatura
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {loading ? (
            <>
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </>
          ) : cards.length > 0 ? (
            cards.map((card) => (
              <Card key={card.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex gap-3">
                      <div className="mt-1">
                        {getBrandIcon(card.brand)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium">{card.brand}</p>
                          {card.is_default && (
                            <Badge variant="secondary" className="text-xs">
                              Padrão
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-1">
                          •••• •••• •••• {card.last_four_digits}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {card.holder_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Validade: {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <CreditCard className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nenhum cartão cadastrado</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
